import { sizesForColor, colorsForSize, findVariant } from "../utils/groupProducts";

/**
 * Size chips when listing is grouped by color; color chips when grouped by size.
 */
function ProductVariantPicker({
  variants,
  product,
  groupBy,
  onSelect,
}) {
  if (!variants || variants.length <= 1 || !product) return null;

  const currentSize = product.boxSizeLabel || product.fabric;
  const currentColor = product.color;

  if (groupBy === "color") {
    const sizes = sizesForColor(variants, currentColor);
    if (sizes.length <= 1) return null;

    return (
      <div className="product-variant-picker mb-3">
        <p className="mb-2 small">
          <b>Choose size:</b>
        </p>
        <div className="product-variant-picker__options" role="listbox" aria-label="Box size">
          {sizes.map((size) => {
            const match = findVariant(variants, { color: currentColor, sizeLabel: size });
            const selected = size === currentSize;
            const outOfStock = match && (match.stock || 0) <= 0;

            return (
              <button
                key={size}
                type="button"
                role="option"
                aria-selected={selected}
                className={`product-variant-picker__chip${selected ? " is-selected" : ""}${outOfStock ? " is-disabled" : ""}`}
                disabled={outOfStock}
                onClick={() => match && onSelect(match)}
              >
                {size}
                {outOfStock ? " (out of stock)" : ""}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (groupBy === "size") {
    const colors = colorsForSize(variants, currentSize);
    if (colors.length <= 1) return null;

    return (
      <div className="product-variant-picker mb-3">
        <p className="mb-2 small">
          <b>Choose color:</b>
        </p>
        <div className="product-variant-picker__options" role="listbox" aria-label="Box color">
          {colors.map((color) => {
            const match = findVariant(variants, { color, sizeLabel: currentSize });
            const selected = color === currentColor;
            const outOfStock = match && (match.stock || 0) <= 0;

            return (
              <button
                key={color}
                type="button"
                role="option"
                aria-selected={selected}
                className={`product-variant-picker__chip${selected ? " is-selected" : ""}${outOfStock ? " is-disabled" : ""}`}
                disabled={outOfStock}
                onClick={() => match && onSelect(match)}
              >
                {color}
                {outOfStock ? " (out of stock)" : ""}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const allColors = [...new Set(variants.map((v) => v.color).filter(Boolean))];
  const allSizes = [...new Set(variants.map((v) => v.boxSizeLabel || v.fabric).filter(Boolean))];

  const showColor = allColors.length > 1;
  const showSize = allSizes.length > 1;

  if (!showColor && !showSize) return null;

  const handleColorPick = (color) => {
    const match =
      findVariant(variants, { color, sizeLabel: currentSize }) ||
      variants.find((v) => v.color === color);
    if (match) onSelect(match);
  };

  const handleSizePick = (size) => {
    const match =
      findVariant(variants, { color: currentColor, sizeLabel: size }) ||
      variants.find((v) => (v.boxSizeLabel || v.fabric) === size);
    if (match) onSelect(match);
  };

  return (
    <div className="product-variant-picker mb-3">
      {showColor ? (
        <>
          <p className="mb-2 small">
            <b>Choose color:</b>
          </p>
          <div className="product-variant-picker__options mb-3" role="listbox" aria-label="Box color">
            {allColors.map((color) => {
              const match = findVariant(variants, { color, sizeLabel: currentSize }) || variants.find((v) => v.color === color);
              const selected = color === currentColor;
              const outOfStock = match && (match.stock || 0) <= 0;
              return (
                <button
                  key={color}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`product-variant-picker__chip${selected ? " is-selected" : ""}${outOfStock ? " is-disabled" : ""}`}
                  disabled={outOfStock}
                  onClick={() => handleColorPick(color)}
                >
                  {color}
                  {outOfStock ? " (out of stock)" : ""}
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {showSize ? (
        <>
          <p className="mb-2 small">
            <b>Choose size:</b>
          </p>
          <div className="product-variant-picker__options" role="listbox" aria-label="Box size">
            {allSizes.map((size) => {
              const match = findVariant(variants, { color: currentColor, sizeLabel: size }) || variants.find((v) => (v.boxSizeLabel || v.fabric) === size);
              const selected = size === currentSize;
              const outOfStock = match && (match.stock || 0) <= 0;
              return (
                <button
                  key={size}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`product-variant-picker__chip${selected ? " is-selected" : ""}${outOfStock ? " is-disabled" : ""}`}
                  disabled={outOfStock}
                  onClick={() => handleSizePick(size)}
                >
                  {size}
                  {outOfStock ? " (out of stock)" : ""}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default ProductVariantPicker;
